import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import type { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Currency } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Container } from '@sorare/core/src/atoms/layout/Container';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM, BrandS, HeadlineXL } from '@sorare/core/src/atoms/typography';
import { useConnectionContext } from '@sorare/core/src/contexts/connection';
import { useIsMobile } from '@sorare/core/src/hooks/device/useIsMobile';
import { useAmountWithConversion } from '@sorare/core/src/hooks/useAmountWithConversion';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { glossary } from '@sorare/core/src/lib/glossary';
import { OPTIMISTIC_SLOTS_COUNT_PER_SPORT } from '@sorare/core/src/lib/so5';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { HowItWorks } from '#src/components/invite/HowItWorks/index.tsx';

import type { OfflineLanding_config } from './__generated__/index.graphql.ts';
import illustration from './assets/illustration.webp';

const Root = styled.section`
  padding: calc(8 * var(--unit)) 0;
  background: radial-gradient(
      circle at top left,
      rgb(14, 116, 91) 0%,
      transparent 30%
    ),
    radial-gradient(
      circle at bottom right,
      rgba(14, 116, 91, 0.5) 0%,
      transparent 20%
    ),
    radial-gradient(
      circle at top right,
      #7001fe 0%,
      #7001fe 10%,
      rgb(0, 0, 0) 60%
    );
  background-attachment: fixed;
  @media ${tabletAndAbove} {
    padding-top: 0;
  }
`;
const MainBlock = styled(Vertical)`
  text-align: center;
  &.left {
    text-align: left;
    align-items: flex-start;
  }
  @media ${tabletAndAbove} {
    min-height: 60vh;
  }
`;
const Block = styled(Vertical)`
  text-align: center;
  background: rgba(255, 255, 255, 0.05);
  border-radius: var(--double-unit);
  padding: var(--double-unit);
  @media ${tabletAndAbove} {
    min-height: 90%;
  }
`;
const Title = styled.div`
  font-size: var(--fs-landing-title);
  font-weight: normal;
`;
const Img = styled.img`
  border-radius: var(--double-unit);
  max-width: 100%;
  height: auto;
`;

const CtasWrapper = styled(Horizontal)`
  flex-wrap: wrap;
  width: 100%;
`;

const Ctas = () => {
  const isMobile = useIsMobile();
  const { signIn, signUp } = useConnectionContext();
  return (
    <CtasWrapper center>
      <Button
        fullWidth={isMobile}
        size="medium"
        color="tertiary"
        onClick={signUp}
      >
        <FormattedMessage {...glossary.signup} />
      </Button>
      <Button
        fullWidth={isMobile}
        size="medium"
        color="primary"
        onClick={signIn}
      >
        <FormattedMessage {...glossary.signin} />
      </Button>
    </CtasWrapper>
  );
};

type Props = {
  config?: OfflineLanding_config;
  sport: Sport;
};
export const OfflineLanding = ({ config, sport }: Props) => {
  const { referralProgramConfig } = config || {};
  const { toMonetaryAmount } = useMonetaryAmount();
  const { main: amount } = useAmountWithConversion({
    monetaryAmount: toMonetaryAmount(referralProgramConfig?.maxDiscount),
    primaryCurrency: Currency.FIAT,
    hideFractionDigits: true,
  });
  const cardsToBuy =
    referralProgramConfig?.numberCardsToBuy ||
    OPTIMISTIC_SLOTS_COUNT_PER_SPORT[sport];

  return (
    <Root>
      <Container maxWidth={1000}>
        <Vertical gap={3}>
          <MainBlock center gap={3}>
            <Vertical gap={2}>
              <div>
                <BrandS as="p" color="var(--c-nd-600)" bold>
                  <FormattedMessage defaultMessage="Referral program" />
                </BrandS>
                <Title as="h1">
                  <FormattedMessage
                    defaultMessage="Invite your friends to Sorare and get {amount} credit."
                    values={{
                      amount,
                    }}
                  />
                </Title>
              </div>
              <BodyM as="p" color="var(--c-nd-600)">
                <FormattedMessage defaultMessage="You're just steps away from your reward." />
              </BodyM>
            </Vertical>
            <Ctas />
          </MainBlock>
          <Img src={illustration} alt="" width="1280px" height="700px" />
          <MainBlock gap={2} center className="left">
            <HeadlineXL as="h2">
              <FormattedMessage defaultMessage="How it works?" />
            </HeadlineXL>
            <HowItWorks amount={amount} cardsToBuy={cardsToBuy} />
          </MainBlock>
          <MainBlock center gap={3}>
            <Block center gap={3}>
              <Vertical gap={2}>
                <Title as="h2">
                  <FormattedMessage defaultMessage="Ready to start your Sorare journey?" />
                </Title>
                <BodyM as="p" color="var(--c-nd-600)">
                  <FormattedMessage defaultMessage="You're just steps away from your reward." />
                </BodyM>
              </Vertical>
              <Ctas />
            </Block>
          </MainBlock>
        </Vertical>
      </Container>
    </Root>
  );
};

OfflineLanding.fragments = {
  config: gql`
    fragment OfflineLanding_config on Config {
      id
      defaultFiatCurrency
      referralProgramConfig(sport: $sport) {
        id
        numberCardsToBuy
        maxDiscount {
          ...useMonetaryAmount_monetaryAmount
        }
      }
    }
    ${useMonetaryAmount.fragments.monetaryAmount}
  ` as TypedDocumentNode<OfflineLanding_config>,
};
